import React, { useState, useEffect } from 'react';
import ActivitySelector from './common/ActivitySelector';

const EditProgramModal = ({ program, isOpen, onClose, onSave, levels, activities }) => {
    const [editData, setEditData] = useState({
        name: '',
        description: '',
        shortDescription: '',
        levelId: '',
        image: null,
        imageUrl: '',
        weeks: {},
        isFree: false,
        isPublished: false
    });

    const [expandedModalWeeks, setExpandedModalWeeks] = useState({});
    const [expandedModalDays, setExpandedModalDays] = useState({});

    const toggleModalWeek = (weekName) => {
        setExpandedModalWeeks(prev => ({
            ...prev,
            [weekName]: !prev[weekName]
        }));
    };

    const toggleModalDay = (weekName, dayName) => {
        setExpandedModalDays(prev => ({
            ...prev,
            [`${weekName}-${dayName}`]: !prev[`${weekName}-${dayName}`]
        }));
    };

    useEffect(() => {
        if (program) {
            console.log(program, "PROGRAM");
            setEditData({
                name: program.name || '',
                description: program.description || '',
                shortDescription: program.short_description || '',
                levelId: program.level || '',
                image: null,
                imageUrl: program.image_url || '',
                weeks: program.weeks || {},
                isFree: program.isFree === 1,
                isPublished: program.isPublished === 1
            });
        }
    }, [program]);

    const addWeek = () => {
        const updatedWeeks = { ...editData.weeks };
        const weekNumber = Object.keys(updatedWeeks).length + 1;
        const newWeekName = `Week ${weekNumber}`;
        
        updatedWeeks[newWeekName] = {
            name: newWeekName,
            description: '',
            days: {}
        };
        
        setEditData({
            ...editData,
            weeks: updatedWeeks
        });
    };

    const addDay = (weekName) => {
        const updatedWeeks = { ...editData.weeks };
        const dayNumber = Object.keys(updatedWeeks[weekName].days).length + 1;
        const newDayName = `Day ${dayNumber}`;
        
        updatedWeeks[weekName].days[newDayName] = {
            name: newDayName,
            description: '',
            activities: []
        };
        
        setEditData({
            ...editData,
            weeks: updatedWeeks
        });
    };

    const removeWeek = (weekName) => {
        const updatedWeeks = { ...editData.weeks };
        delete updatedWeeks[weekName];
        
        // Renumber the remaining weeks to maintain consecutive order
        const sortedWeekNames = Object.keys(updatedWeeks)
          .sort((a, b) => {
            const aNum = parseInt(a.split(' ')[1]);
            const bNum = parseInt(b.split(' ')[1]);
            return aNum - bNum;
          });
        
        const reorderedWeeks = {};
        
        sortedWeekNames.forEach((oldWeekName, index) => {
          const newWeekNumber = index + 1;
          const newWeekName = `Week ${newWeekNumber}`;
          
          // If the week name already matches the new name, keep it as is
          if (oldWeekName === newWeekName) {
            reorderedWeeks[newWeekName] = updatedWeeks[oldWeekName];
          } else {
            // Otherwise, rename the week
            reorderedWeeks[newWeekName] = {
              ...updatedWeeks[oldWeekName],
              name: newWeekName
            };
          }
        });
        
        setEditData({
          ...editData,
          weeks: reorderedWeeks
        });
    };

    const removeDay = (weekName, dayName) => {
        const updatedWeeks = { ...editData.weeks };
        delete updatedWeeks[weekName].days[dayName];
        
        // Renumber the remaining days to maintain consecutive order
        const sortedDayNames = Object.keys(updatedWeeks[weekName].days)
          .sort((a, b) => {
            const aNum = parseInt(a.split(' ')[1]);
            const bNum = parseInt(b.split(' ')[1]);
            return aNum - bNum;
          });
        
        const reorderedDays = {};
        
        sortedDayNames.forEach((oldDayName, index) => {
          const newDayNumber = index + 1;
          const newDayName = `Day ${newDayNumber}`;
          
          // If the day name already matches the new name, keep it as is
          if (oldDayName === newDayName) {
            reorderedDays[newDayName] = updatedWeeks[weekName].days[oldDayName];
          } else {
            // Otherwise, rename the day
            reorderedDays[newDayName] = {
              ...updatedWeeks[weekName].days[oldDayName],
              name: newDayName
            };
          }
        });
        
        updatedWeeks[weekName].days = reorderedDays;
        
        setEditData({
          ...editData,
          weeks: updatedWeeks
        });
    };

    const removeActivity = (weekName, dayName, activityIndex) => {
        const updatedWeeks = { ...editData.weeks };
        updatedWeeks[weekName].days[dayName].activities.splice(activityIndex, 1);
        setEditData({
            ...editData,
            weeks: updatedWeeks
        });
    };

    const handleActivityAdd = (weekName, dayName, activity) => {
        console.log("Adding activity:", activity);
        const updatedWeeks = { ...editData.weeks };
        if (!updatedWeeks[weekName].days[dayName].activities) {
            updatedWeeks[weekName].days[dayName].activities = [];
        }

        // Handle both possible ID field names and ensure we have a valid ID
        const activityId = activity.idactivity || activity.idactivities || activity.id;
        if (!activityId) {
            console.error("No valid activity ID found:", activity);
            return;
        }

        // Create a new activity object with consistent structure
        const newActivity = {
            idactivity: activityId,
            name: activity.name,
            description: activity.description || "",
            instanceId: Date.now()
        };

        updatedWeeks[weekName].days[dayName].activities.push(newActivity);
        setEditData({
            ...editData,
            weeks: updatedWeeks
        });
    };

    const renderWeeks = () => {
        return (
            <div className="weeks-container">
                <div className="weeks-header">
                    <h3>Program Schedule</h3>
                    <button type="button" onClick={addWeek} className="add-button">
                        <i className="fas fa-plus"></i> Add Week
                    </button>
                </div>
                
                {Object.entries(editData.weeks || {}).map(([weekName, weekData]) => (
                    <div key={weekName} className="week-editor">
                        <div 
                            className="week-header"
                            onClick={() => toggleModalWeek(weekName)}
                            style={{ cursor: 'pointer' }}
                        >
                            <h4>
                                <i className={`fas fa-chevron-${expandedModalWeeks[weekName] ? 'down' : 'right'}`} style={{ marginRight: '10px' }} />
                                {weekData.name}
                            </h4>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeWeek(weekName);
                                }}
                                className="remove-button"
                            >
                                <i className="fas fa-trash"></i>
                            </button>
                        </div>
                        
                        {expandedModalWeeks[weekName] && (
                            <>
                                <div className="form-group">
                                    <label>Week Description</label>
                                    <input
                                        type="text"
                                        value={weekData.description || ''}
                                        onChange={(e) => {
                                            const updatedWeeks = { ...editData.weeks };
                                            updatedWeeks[weekName].description = e.target.value;
                                            setEditData({ ...editData, weeks: updatedWeeks });
                                        }}
                                        placeholder="Week description"
                                    />
                                </div>

                                <div className="days-container">
                                    <button type="button" onClick={() => addDay(weekName)} className="add-button">
                                        <i className="fas fa-plus"></i> Add Day
                                    </button>
                                    
                                    {Object.entries(weekData.days || {}).map(([dayName, dayData]) => (
                                        <div key={dayName} className="day-editor">
                                            <div 
                                                className="day-header"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleModalDay(weekName, dayName);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <h5>
                                                    <i className={`fas fa-chevron-${expandedModalDays[`${weekName}-${dayName}`] ? 'down' : 'right'}`} style={{ marginRight: '10px' }} />
                                                    {dayData.name}
                                                </h5>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeDay(weekName, dayName);
                                                    }}
                                                    className="remove-button"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>

                                            {expandedModalDays[`${weekName}-${dayName}`] && (
                                                <>
                                                    <div className="form-group">
                                                        <label>Day Description</label>
                                                        <input
                                                            type="text"
                                                            value={dayData.description || ''}
                                                            onChange={(e) => {
                                                                const updatedWeeks = { ...editData.weeks };
                                                                updatedWeeks[weekName].days[dayName].description = e.target.value;
                                                                setEditData({ ...editData, weeks: updatedWeeks });
                                                            }}
                                                            placeholder="Day description"
                                                        />
                                                    </div>

                                                    <div className="activities-section">
                                                        <h6>Activities</h6>
                                                        <ActivitySelector
                                                            dayNumber={dayData.name}
                                                            activities={activities}
                                                            selectedActivities={dayData.activities || []}
                                                            onActivityAdd={(activity) => handleActivityAdd(weekName, dayName, activity)}
                                                            onActivityRemove={(instanceId) => removeActivity(weekName, dayName, instanceId)}
                                                        />
{/*                                                         
                                                        <div className="activities-list">
                                                            {dayData.activities?.map((activity, index) => (
                                                                <div key={index} className="activity-item">
                                                                    <span>{activity.name}</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeActivity(weekName, dayName, index)}
                                                                        className="remove-button"
                                                                    >
                                                                        <i className="fas fa-times"></i>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div> */}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Edit Program: {program.name}</h2>
                    <button className="close-button" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="modal-body">
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        console.log(editData);
                        onSave(editData);
                    }}>
                        <div className="form-group">
                            <label>Program Name</label>
                            <input
                                type="text"
                                value={editData.name}
                                onChange={(e) => setEditData({
                                    ...editData,
                                    name: e.target.value
                                })}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Short Description</label>
                            <input
                                type="text"
                                value={editData.shortDescription}
                                onChange={(e) => setEditData({
                                    ...editData,
                                    shortDescription: e.target.value
                                })}
                                maxLength="255"
                            />
                        </div>

                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                value={editData.description}
                                onChange={(e) => setEditData({
                                    ...editData,
                                    description: e.target.value
                                })}
                                rows="4"
                            />
                        </div>

                        <div className="form-group">
                            <label>Program Image</label>
                            {editData.imageUrl && (
                                <div className="current-image">
                                    <img 
                                        src={editData.imageUrl} 
                                        alt="Current program" 
                                        className="preview-image"
                                    />
                                    <span>Current image</span>
                                </div>
                            )}
                            <input
                                type="file"
                                onChange={(e) => setEditData({
                                    ...editData,
                                    image: e.target.files[0]
                                })}
                                accept="image/*"
                            />
                        </div>

                        <div className="form-group">
                            <label>Intensity Level</label>
                            <select
                                value={editData.levelId}
                                onChange={(e) => setEditData({
                                    ...editData,
                                    levelId: e.target.value
                                })}
                            >
                                <option value="">Select a level</option>
                                {levels.map(level => (
                                    <option
                                        key={level.idintensity_zone}
                                        value={level.idintensity_zone}
                                        style={{
                                            backgroundColor: level.fargekode_bakgrunn,
                                            color: level.fargekode_tekst
                                        }}
                                    >
                                        {level.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="toggle-label">
                                <span>Free Program</span>
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={editData.isFree}
                                        onChange={(e) => setEditData({
                                            ...editData,
                                            isFree: e.target.checked
                                        })}
                                    />
                                    <span className="toggle-slider"></span>
                                </div>
                                <span className="toggle-text">{editData.isFree ? 'Free' : 'Premium'}</span>
                            </label>
                        </div>

                        <div className="form-group">
                            <label className="toggle-label">
                                <span>Publish Status</span>
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={editData.isPublished}
                                        onChange={(e) => setEditData({
                                            ...editData,
                                            isPublished: e.target.checked
                                        })}
                                    />
                                    <span className="toggle-slider"></span>
                                </div>
                                <span className="toggle-text">{editData.isPublished ? 'Published' : 'Draft'}</span>
                            </label>
                        </div>

                        <div className="program-schedule-editor">
                            {renderWeeks()}
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="cancel-button" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="save-button">
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.7);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                }

                .modal-content {
                    background: white;
                    border-radius: 8px;
                    padding: 20px;
                    width: 90%;
                    max-width: 800px;
                    max-height: 90vh;
                    overflow-y: auto;
                    position: relative;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #eee;
                }

                .modal-body {
                    margin-bottom: 20px;
                }

                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                    padding-top: 20px;
                    border-top: 1px solid #eee;
                }

                .current-image {
                    margin-top: 10px;
                    text-align: center;
                }

                .current-image img {
                    max-width: 200px;
                    border-radius: 4px;
                    margin-bottom: 5px;
                }

                .week-editor, .day-editor {
                    background: #f8f9fa;
                    padding: 15px;
                    margin: 10px 0;
                    border-radius: 8px;
                }

                .days-editor {
                    margin-left: 20px;
                }

                .week-section {
                    margin-bottom: 20px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    padding: 12px;
                }

                .week-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                    padding: 8px;
                    cursor: pointer;
                }

                .week-header h4 {
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .week-header i {
                    font-size: 14px;
                    width: 14px;
                    color: #666;
                }

                .days-grid {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    padding: 0 8px;
                }

                .day-card {
                    background: white;
                    border-radius: 6px;
                    padding: 12px;
                    border: 1px solid #eee;
                }

                .day-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                    cursor: pointer;
                }

                .day-header h5 {
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .day-header i {
                    font-size: 12px;
                    width: 12px;
                    color: #666;
                }

                .activities-list {
                    margin-top: 10px;
                }

                .activity-card {
                    background: #f8f9fa;
                    border-radius: 4px;
                    padding: 8px 12px;
                    margin-bottom: 8px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .activity-name {
                    font-size: 14px;
                    color: #333;
                    margin: 0;
                }

                .activity-description {
                    font-size: 13px;
                    color: #666;
                    margin: 4px 0 0 0;
                }

                .no-activities {
                    color: #666;
                    font-style: italic;
                    text-align: center;
                    padding: 8px;
                    font-size: 13px;
                }

                .remove-button {
                    background: none;
                    border: none;
                    color: #dc3545;
                    cursor: pointer;
                    padding: 4px 8px;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                }

                .remove-button:hover {
                    background: #fee2e2;
                }

                .week-description-text,
                .day-description-text {
                    color: #666;
                    font-size: 13px;
                    margin: 4px 0 8px 0;
                    padding: 0 8px;
                }
            `}</style>
      </div>
    );
  };

export default EditProgramModal;